import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { encountersApi } from "../../../shared/api/encounters";
import {
  clinicFeaturesApi,
  clinicFeaturesQueryKeys,
} from "../../../shared/api/clinicFeatures";
import { apiError } from "../../../shared/api/client";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Hình thức khám (Dịch vụ / BHYT) for a visit. Only shown when the clinic has
 * BHYT enabled (Cài đặt → Phòng khám → Hình thức khám). Editable while the visit
 * is open; locked (read-only) once completed/signed. (ADR-0027 Phase 2)
 */
export function ExamModePanel({ encounterId }: { encounterId: string }) {
  const qc = useQueryClient();
  const features = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const key = ["encounter", encounterId, "exam-mode"] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: () => encountersApi.getExamMode(encounterId),
  });

  const [card, setCard] = useState("");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setCard(q.data?.bhytCardNo ?? "");
  }, [q.data?.bhytCardNo]);

  const save = useMutation({
    mutationFn: (v: { examType: "SERVICE" | "BHYT"; bhytCardNo: string | null }) =>
      encountersApi.updateExamMode(encounterId, v),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      setErr(null);
    },
    onError: (e) => setErr(apiError(e).detail),
  });

  // Service-only clinic (BHYT off) → the whole exam-mode concept is hidden.
  if (!features.data?.bhytEnabled || !q.data) return null;

  const { examType, locked } = q.data;
  const isBhyt = examType === "BHYT";

  return (
    <div className="rounded-lg border bg-card p-3 text-card-foreground">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-foreground">Hình thức khám</span>
        <div className="inline-flex overflow-hidden rounded-md border">
          {(["SERVICE", "BHYT"] as const).map((t) => (
            <button
              key={t}
              type="button"
              disabled={locked || save.isPending}
              onClick={() =>
                examType !== t &&
                save.mutate({ examType: t, bhytCardNo: t === "BHYT" ? card.trim() || null : null })
              }
              className={cn(
                "px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed",
                examType === t
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-muted",
              )}
            >
              {t === "SERVICE" ? "Dịch vụ" : "BHYT"}
            </button>
          ))}
        </div>

        {isBhyt && (
          <div className="flex items-center gap-2">
            <label htmlFor="bhyt-card" className="text-sm text-muted-foreground">
              Số thẻ BHYT
            </label>
            <Input
              id="bhyt-card"
              value={card}
              disabled={locked}
              maxLength={32}
              placeholder="VD: DN4790123456789"
              className="h-9 w-56"
              onChange={(e) => setCard(e.target.value)}
              onBlur={() => {
                if ((card.trim() || null) !== (q.data?.bhytCardNo ?? null))
                  save.mutate({ examType: "BHYT", bhytCardNo: card.trim() || null });
              }}
            />
          </div>
        )}

        {locked && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock size={12} /> Đã khóa (lượt khám đã kết thúc)
          </span>
        )}
      </div>
      {err && <p className="mt-2 text-sm text-destructive">{err}</p>}
    </div>
  );
}
